// Layer 3 (Transformation): metallurgist patterns panel — SPC patterns + candidate causes.

import type { ExcursionRuleKind } from '@/versions/v1/schema/spc';
import type { SignalRole } from '@/versions/v1/schema/tag-mapping';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { excursions } from '@/versions/v1/shared/SpcEngine';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { liveRuleSet } from '@/versions/v1/shared/RuleSetEngine';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import { readingByIdLookup, formatValue } from '@/versions/v1/shared/reading.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';

const MET_PATTERN_ROLES: readonly SignalRole[] = [
  'pb-rougher-froth',
  'zn-rougher-froth',
  'pb-rougher-ph',
  'zn-conditioner-ph',
  'grind-ph',
  'zn-cond-residence',
];

function patternLabel(rule: ExcursionRuleKind): string {
  switch (rule) {
    case 'cyclical':
      return 'cyclical';
    case 'six-point-trend':
      return 'six rising';
    case 'beyond-limit':
      return 'beyond limit';
    case 'widening-variability':
      return 'widening';
    default:
      return rule;
  }
}

function variableForRole(role: SignalRole): (typeof MONITORED_VARIABLES)[number] | undefined {
  const mapping = TAG_MAPPING.find((row) => row.role === role);
  if (!mapping?.seriesId) return undefined;
  return MONITORED_VARIABLES.find((row) => row.seriesId === mapping.seriesId);
}

function variableLabel(variableId: string): string {
  const variable = MONITORED_VARIABLES.find((row) => row.id === variableId);
  const reading = readingByIdLookup(variable?.readingId ?? '');
  return reading?.label ?? variableId.replace(/^mv-/, '');
}

function rankCauses(
  role: SignalRole,
  advisory: AdvisorySessionState,
  subprocessId?: string,
): DiagnosticRule[] {
  const { rules } = liveRuleSet(advisory);
  const matched = rules.filter((row) => row.when?.some((clause) => clause.signal === role));
  return matched.sort((left, right) => {
    const leftSub = left.subprocessId === subprocessId ? 0 : 1;
    const rightSub = right.subprocessId === subprocessId ? 0 : 1;
    if (leftSub !== rightSub) return leftSub - rightSub;
    const leftPrimary = left.when?.[0]?.signal === role ? 0 : 1;
    const rightPrimary = right.when?.[0]?.signal === role ? 0 : 1;
    if (leftPrimary !== rightPrimary) return leftPrimary - rightPrimary;
    return left.id.localeCompare(right.id);
  });
}

function candidateCauses(
  role: SignalRole,
  advisory: AdvisorySessionState,
  subprocessId?: string,
): { prose: string; ruleId: string }[] {
  return rankCauses(role, advisory, subprocessId)
    .slice(0, 3)
    .map((row) => ({ prose: row.diagnosis, ruleId: row.id }));
}

function stableValueLine(variableId: string, state: SessionState): string | undefined {
  const variable = MONITORED_VARIABLES.find((row) => row.id === variableId);
  if (!variable) return undefined;
  const reading = readingByIdLookup(variable.readingId);
  if (!reading) return undefined;
  const view = seriesAt(variable.seriesId, state);
  const latest = [...view.points].reverse().find((row) => row.value !== null && row.value !== undefined);
  if (latest?.value === null || latest?.value === undefined) return undefined;
  const label = reading.label;
  const value = formatValue(reading, { value: latest.value, withUnit: true });
  return `${label} · ${value}`;
}

export type PatternRow = {
  variableId: string;
  label: string;
  pattern: string;
  valueLine?: string;
  causes: { prose: string; ruleId: string }[];
};

export function metPatternsPanel(
  state: SessionState,
  advisory: AdvisorySessionState,
): PatternRow[] {
  const allExcursions = excursions(state);
  const rows: PatternRow[] = [];

  for (const role of MET_PATTERN_ROLES) {
    const variable = variableForRole(role);
    const variableId = variable?.id ?? (role === 'zn-cond-residence' ? 'mv-zn-cond-residence' : undefined);
    if (!variableId) continue;
    const mapping = TAG_MAPPING.find((row) => row.role === role);
    const excursion =
      allExcursions.find((row) => row.variableId === variableId && row.rule === 'cyclical') ??
      allExcursions.find((row) => row.variableId === variableId);
    const subprocessId = mapping?.subprocessId;
    rows.push({
      variableId,
      label: variableLabel(variableId),
      pattern: excursion ? patternLabel(excursion.rule) : 'stable',
      valueLine: excursion ? undefined : stableValueLine(variableId, state),
      causes: excursion ? candidateCauses(role, advisory, subprocessId) : [],
    });
  }

  return rows;
}
